/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  subirArchivosPesadosS3,
  Titulo,
  Formulario
} from "../../../shared/index.js";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";

// Formulario para registrar una propuesta comercial generada fuera del sistema
// actual sobre un proceso que todavía no tiene propuesta. Genera la propuesta
// (ya aceptada) y las filas VIGENTE de PrecioActividadCliente con los precios
// ingresados. Usa el componente Formulario compartido.
export default function PropuestaHistorica({ proceso, onClose, onCreada }) {
  const [loader, setLoader] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [config, setConfig] = useState(null);
  const toast = useRef(null);

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/propuesta/historica/proceso/${proceso.id}`,
      null,
      (data) => {
        setConfig(construirConfig(data));
        setLoader(false);
      },
      (r) => {
        setLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r[0]?.msg || "Error al cargar los datos",
          sticky: true
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proceso.id]);

  function construirConfig(data) {
    const usuariosAdmin = (data.usuariosAdmin || []).map((u) => ({
      value: u.id,
      etiqueta: u.nombre
    }));
    const actividades = data.actividades || [];

    const campos = [
      {
        nombre: "fechacreacion",
        etiqueta: "Fecha de creación",
        tipoitemformularioid: 7,
        requerido: true
      },
      {
        nombre: "usuarioemisorid",
        etiqueta: "Usuario emisor",
        tipoitemformularioid: 5,
        opciones: usuariosAdmin,
        sinOpcionInicial: true,
        opcionVacia: true
      },
      {
        nombre: "fechaaceptacion",
        etiqueta: "Fecha de aceptación",
        tipoitemformularioid: 7,
        requerido: true
      },
      {
        nombre: "nombreAceptador",
        etiqueta: "Aceptador",
        tipoitemformularioid: 1
      },
      {
        nombre: "archivo",
        etiqueta: "Archivo",
        tipoitemformularioid: 10
      },
      ...actividades.map((a) => ({
        nombre: `precio_${a.actividadTipoId}`,
        etiqueta: a.nombre,
        tipoitemformularioid: 14,
        respuesta: { precio: null, monedaId: a.monedaId ?? null }
      }))
    ];

    return {
      secciones: [{ campos }],
      botones: [
        {
          etiqueta: "Cargar propuesta",
          validar: true,
          confirmacion:
            "¿Registrar esta propuesta comercial histórica? Los precios quedarán vigentes.",
          callback: (contenido, archivo) =>
            enviar(contenido, actividades, archivo)
        }
      ]
    };
  }

  function enviar(contenido, actividades, archivo) {
    setGuardando(true);
    accessAPI(
      "POST",
      "admin/propuesta/historica",
      {
        procesoId: proceso.id,
        fechacreacion: contenido.fechacreacion
          ? new Date(contenido.fechacreacion).toISOString()
          : null,
        fechaaceptacion: contenido.fechaaceptacion
          ? new Date(contenido.fechaaceptacion).toISOString()
          : null,
        usuarioemisorid: contenido.usuarioemisorid || null,
        nombreAceptador: contenido.nombreAceptador || null,
        precios: actividades.map((a) => {
          // El campo de precio (tipo 14) emite { precio, monedaId }. Un campo
          // en blanco → precio null (no se registra como 0); un 0 explícito
          // viaja como 0 y se registra como precio bonificado.
          const valor = contenido[`precio_${a.actividadTipoId}`] || {};
          return {
            actividadTipoId: a.actividadTipoId,
            precio: valor.precio ?? null,
            monedaId: valor.monedaId ?? a.monedaId ?? null
          };
        })
      },
      (r) => {
        const propuestaId = r.propuestaComercial?.id;
        if (archivo && propuestaId) {
          subirArchivo(propuestaId, archivo)
            .then(() => creada(r))
            .catch((error) => {
              setGuardando(false);
              toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: `La propuesta se registró, pero no se pudo subir el archivo: ${
                  error?.[0]?.msg || "error desconocido"
                }`,
                sticky: true
              });
            });
        } else {
          creada(r);
        }
      },
      (r) => {
        setGuardando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r[0]?.msg || "Error al cargar la propuesta",
          sticky: true
        });
      }
    );
  }

  // Sube el archivo directo a S3 y lo adjunta a la propuesta recién creada.
  function subirArchivo(propuestaId, archivo) {
    return subirArchivosPesadosS3({
      presignEndpoint: "admin/archivo/presign",
      confirmarEndpoint: `admin/propuesta/${propuestaId}/archivo/confirmar`,
      archivos: archivo,
      datosPresign: { ubicacion: "propuestas" }
    });
  }

  function creada(r) {
    setGuardando(false);
    const planificaciones = r.refacturacion?.planificaciones || 0;
    toast.current?.show({
      severity: "success",
      summary: "Propuesta cargada",
      detail:
        planificaciones > 0
          ? `La propuesta histórica se registró correctamente. Se ${
              planificaciones === 1
                ? "generó 1 planificación de factura retroactiva"
                : `generaron ${planificaciones} planificaciones de factura retroactivas`
            }.`
          : "La propuesta histórica se registró correctamente. No se generaron planificaciones de factura retroactivas.",
      life: 6000
    });
    onCreada?.(r.propuestaComercial?.id);
  }

  if (loader) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <Toast ref={toast} position="top-center" />
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} width="100%" height="2.5rem" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} position="top-center" />
      <Titulo>Cargar propuesta histórica</Titulo>
      <p css={{ color: "var(--grisTextos)", marginTop: "-0.5em" }}>
        Proceso {proceso.nombre} — {proceso.nombrefantasia}
      </p>
      {config && <Formulario config={config} loader={guardando} />}
    </div>
  );
}
